import React from "react"
import { Link } from "react-router-dom"
import { Field, FieldArray } from "formik"

import CatchCard from "~/library/CatchCard"
import LoadingWrapper from "~/library/LoadingWrapper"

import * as Yup from "yup"
import FormikWrapper from "~/library/FormikWrapper"
import FormikInput from "~/library/FormikInput"
import Menu from "~/types/Menu"
import { apiLineItems } from "~/routes"
import { formatCurrency } from "~/utils/helpers"
import useFetchResource from "~/hooks/useFetchResource"
import LineItem from "~/types/LineItem"

const menuSchema = Yup.object({
  name: Yup.string().required("Menu name is required"),
  active: Yup.boolean(),
  lineItemIds: Yup.array().of(Yup.string()),
})

interface MenuFormProps {
  venueSlug: string
  onSubmit: (values: any) => void
  initialValues?: Menu
  isEditing?: boolean
}

interface GroupedLineItems {
  [categoryName: string]: any[]
}
const MenuForm: React.FC<MenuFormProps> = ({
  venueSlug,
  onSubmit,
  initialValues,
  isEditing = false,
}) => {
  const { data: groupedLineItems, isLoading } = useFetchResource<
    { groupedLineItems: GroupedLineItems },
    "groupedLineItems"
  >(apiLineItems.groupedByCategory.path({ venueSlug }), "groupedLineItems")

  const defaultValues = {
    name: "",
    active: true,
    lineItemIds: [],
  }

  const handleFormSubmit = (values: Menu) => {
    const formData = {
      name: values.name,
      active: values.active,
      line_item_ids: values.lineItemIds,
    }
    onSubmit(formData)
  }

  const getCurrentLineItemIds = (): string[] => {
    if (isEditing && initialValues?.lineItems) {
      return initialValues.lineItems.map((item) => item.uuid)
    }
    return []
  }

  const currentLineItemIds = getCurrentLineItemIds()
  const formInitialValues = isEditing
    ? { ...initialValues, lineItemIds: currentLineItemIds }
    : defaultValues

  // Safe access to grouped line items
  const safeGroupedLineItems: GroupedLineItems = groupedLineItems
    ? (groupedLineItems as unknown as GroupedLineItems)
    : {}

  return (
    <LoadingWrapper isLoading={isLoading}>
      <FormikWrapper
        onSubmit={handleFormSubmit}
        validationSchema={menuSchema}
        initialValues={formInitialValues}
      >
        <CatchCard>
          <FormikInput name="name" label="Menu Name" required helpText="" />

          <div className="mb-3">
            <Field name="active">
              {({ field }: any) => (
                <div className="form-check">
                  <input
                    {...field}
                    type="checkbox"
                    className="form-check-input"
                    id="active"
                    checked={field.value}
                  />
                  <label htmlFor="active" className="form-check-label">
                    Active
                  </label>
                  <div className="form-text">
                    When active, this menu will be available for line items
                  </div>
                </div>
              )}
            </Field>
          </div>
        </CatchCard>

        <CatchCard title="Line Items">
          <FieldArray name="lineItemIds">
            {({ push, remove }) => (
              <div>
                {Object.keys(safeGroupedLineItems).map((categoryName) => (
                  <CategorySection
                    key={categoryName}
                    categoryName={categoryName || "Uncategorized"}
                    items={safeGroupedLineItems[categoryName]}
                    push={push}
                    remove={remove}
                  />
                ))}
              </div>
            )}
          </FieldArray>
        </CatchCard>

        <div className="d-flex">
          <div className="ms-auto">
            <Link className="btn btn-secondary me-2" to="/menus">
              Cancel
            </Link>
            <button type="submit" className="btn btn-primary">
              {isEditing ? "Update Menu" : "Create Menu"}
            </button>
          </div>
        </div>
      </FormikWrapper>
    </LoadingWrapper>
  )
}

interface CategorySectionProps {
  categoryName: string
  items: LineItem[]
  push: (item: string) => void
  remove: (index: number) => void
}

const CategorySection: React.FC<CategorySectionProps> = ({ categoryName, items, push, remove }) => {
  const itemCount = items?.length || 0

  if (itemCount === 0) return null

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
        <h6 className="fw-bold text-dark mb-0 ms-1">{categoryName}</h6>
        <span className="badge bg-primary bg-opacity-10 text-primary ms-2">
          {itemCount} item{itemCount !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="ms-3">
        {items.map((lineItem) => (
          <LineItemCheckbox key={lineItem.uuid} lineItem={lineItem} push={push} remove={remove} />
        ))}
      </div>
    </div>
  )
}

interface LineItemCheckboxProps {
  lineItem: LineItem
  push: (item: string) => void
  remove: (index: number) => void
}

const LineItemCheckbox: React.FC<LineItemCheckboxProps> = ({ lineItem, push, remove }) => {
  return (
    <Field name="lineItemIds">
      {({ field }: any) => (
        <div className="form-check mb-3 p-3 border rounded-2 bg-light bg-opacity-50">
          <div className="d-flex align-items-start">
            <input
              type="checkbox"
              className="form-check-input ms-0 me-2"
              id={`lineItem-${lineItem.uuid}`}
              checked={field.value.includes(lineItem.uuid)}
              onChange={(e) => {
                if (e.target.checked) {
                  push(lineItem.uuid)
                } else {
                  const index = field.value.indexOf(lineItem.uuid)
                  if (index > -1) {
                    remove(index)
                  }
                }
              }}
            />
            <label htmlFor={`lineItem-${lineItem.uuid}`} className="form-check-label flex-grow-1">
              <div className="d-flex justify-content-between align-items-start w-100">
                <div className="flex-grow-1 me-3">
                  <div className="fw-bold text-dark mb-1">{lineItem.name}</div>
                  {lineItem.minOrderQty && lineItem.minOrderQty > 1 && (
                    <div className="small text-muted">Minimum order of {lineItem.minOrderQty}</div>
                  )}
                </div>
                <div className="text-end">
                  <span className="fw-bold text-primary fs-5">
                    {formatCurrency({
                      cents: lineItem.amount.cents,
                      currency_iso: lineItem.amount.currencyIso,
                    })}
                  </span>
                </div>
              </div>
            </label>
          </div>
        </div>
      )}
    </Field>
  )
}

export default MenuForm
